package com.example.mystybag.common.expected

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.FilterQuality
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import coil3.compose.LocalPlatformContext
import coil3.request.ImageRequest
import com.example.mystybag.core.expected.toByteArray
import com.example.mystybag.sharedmodels.SCImage
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.flow.MutableSharedFlow

private const val WEB_MAP_CONTAINER_ID = "mystybag-map-container"
private const val WEB_MAP_SELECTED_CARD_ID = "mystybag-map-selected-card"
// Merchant location-picker map (jsMain MerchantRegistrationReviewStoreDetailsView)
// uses its own DOM overlay; keep its z-index in sync with the same modal flags.
private const val WEB_MERCHANT_LOCATION_MAP_CONTAINER_ID = "mystybag-merchant-location-map-container"
private const val WEB_MAP_Z_TOP = "2147483647"
private const val WEB_MAP_Z_BEHIND_MASK = "0"

private fun applyWebMapLayerState() {
    val overlayBlocked = (window.asDynamic().__mystybagBottomSheetOpen as? Boolean == true) ||
        (window.asDynamic().__mystybagDialogOpen as? Boolean == true)
    val targetZIndex = if (overlayBlocked) WEB_MAP_Z_BEHIND_MASK else WEB_MAP_Z_TOP
    val targetPointerEvents = if (overlayBlocked) "none" else "auto"

    listOf(WEB_MAP_CONTAINER_ID, WEB_MERCHANT_LOCATION_MAP_CONTAINER_ID).forEach { id ->
        (document.getElementById(id) as? org.w3c.dom.HTMLElement)?.let { container ->
            container.style.setProperty("z-index", targetZIndex, "important")
            container.style.setProperty("pointer-events", targetPointerEvents)
        }
    }
    (document.getElementById(WEB_MAP_SELECTED_CARD_ID) as? org.w3c.dom.HTMLElement)?.let { card ->
        card.style.setProperty("pointer-events", targetPointerEvents)
    }
}

actual fun setWebBottomSheetOpen(isOpen: Boolean) {
    window.asDynamic().__mystybagBottomSheetOpen = isOpen
    applyWebMapLayerState()
}

actual fun setWebDialogOpen(isOpen: Boolean) {
    window.asDynamic().__mystybagDialogOpen = isOpen
    applyWebMapLayerState()
}
actual fun hideWebHomeMapOverlayNow() {
    val mapContainer = document.getElementById("mystybag-map-container") as? org.w3c.dom.HTMLElement
    if (mapContainer != null) {
        mapContainer.classList.remove("visible")
        mapContainer.style.setProperty("display", "none", "important")
        mapContainer.style.setProperty("visibility", "hidden", "important")
        mapContainer.style.setProperty("pointer-events", "none", "important")
    }
    val selectedCard = document.getElementById("mystybag-map-selected-card")
    selectedCard?.parentNode?.removeChild(selectedCard)
}
actual fun showWebHomeMapOverlayNow() {
    val mapContainer = document.getElementById("mystybag-map-container") as? org.w3c.dom.HTMLElement ?: return
    mapContainer.classList.add("visible")
    mapContainer.style.setProperty("display", "block", "important")
    mapContainer.style.setProperty("visibility", "visible", "important")
    mapContainer.style.setProperty("opacity", "1", "important")
    applyWebMapLayerState()
}

actual fun resolvePlatformLinkForAsyncImage(link: String): String {
    val value = link.trim()
    if (value.startsWith("http://") || value.startsWith("https://") || value.startsWith("data:")) return value
    val normalized = if (value.startsWith("/")) value else "/$value"
    return "${window.location.origin}$normalized"
}

@Composable
actual fun BackHandler(enabled: Boolean, onBack: () -> Unit) {}

@Composable
actual fun SetSystemBarsColor() {}

private fun ByteArray.toBase64DataUrl(mime: String = "image/png"): String {
    val binary = buildString { this@toBase64DataUrl.forEach { append(it.toInt().and(0xFF).toChar()) } }
    val base64 = js("btoa").unsafeCast<(String) -> String>()(binary)
    return "data:$mime;base64,$base64"
}

@Composable
actual fun SCImageView(
    image: SCImage?,
    modifier: Modifier,
    alignment: Alignment,
    contentScale: ContentScale,
    colorFilter: ColorFilter?,
    filterQuality: FilterQuality
): Boolean {
    image?.let { img ->
        val dataUrl = remember(img) {
            try {
                img.toByteArray().toBase64DataUrl()
            } catch (_: Throwable) {
                null
            }
        }
        dataUrl?.let { url ->
            val request = ImageRequest.Builder(LocalPlatformContext.current).data(url).build()
            AsyncImage(
                model = request,
                contentDescription = null,
                modifier = modifier,
                contentScale = contentScale,
                alignment = alignment,
                colorFilter = colorFilter,
                filterQuality = filterQuality
            )
        }
    }
    return image != null
}

actual object DarkThemeHelper : DarkThemeInterface {
    private var impl: DarkThemeInterface? = null
    actual fun setup(implementation: DarkThemeInterface) { impl = implementation }
    override fun isDarkThemed(dark: Boolean) { impl?.isDarkThemed(dark) }
}

@Composable
actual fun AppKeyboardFocusManager(sharedFlow: MutableSharedFlow<Boolean>) {}

@Composable
actual fun rememberKeyboardScrollPadding(): Dp = 0.dp
